import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, DialogContent, DialogTitle, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAuthStore } from '@/stores/authStore';
import {
  useBookingNotes,
  useAddBookingNote,
  useDeleteBookingNote,
  useUpdateBookingNote,
  usePinBookingNote,
} from '@/hooks/useBookingNotes';
import { useUpdateBooking } from '@/hooks/useBookings';
import { useNotify } from '@/stores/notificationStore';
import { useUIStore } from '@/stores/uiStore';
import { NotesThread } from './NotesThread';
import { WorkOrderNotesList } from './WorkOrderNotesHistory';
import type { NoteCategory } from '@/types';
import { describeApiError } from '@/lib/describeApiError';

const MIN_DIALOG_WIDTH = 600;
const MIN_DIALOG_HEIGHT = 400;
const VIEWPORT_MARGIN = 40;
const DEFAULT_INPUT_HEIGHT = 140;
const MIN_INPUT_HEIGHT = 80;
const MAX_INPUT_HEIGHT = 400;

const CHAT_CATEGORIES: NoteCategory[] = ['internal', 'manager'];

interface LocalNote {
  category: NoteCategory;
  content: string;
  authorName: string;
  createdAt: string;
}

interface WorkOrderNotesDialogProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  creationMode?: boolean;
  localNotes?: LocalNote[];
  onLocalNoteAdd?: (category: NoteCategory, content: string) => void;
  customerNotes?: string | null;
  onCustomerNotesChange?: (value: string) => void;
}

export function WorkOrderNotesDialog({
  open,
  onClose,
  bookingId,
  creationMode,
  localNotes,
  onLocalNoteAdd,
  customerNotes: customerNotesProp,
  onCustomerNotesChange,
}: WorkOrderNotesDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isMobile = useIsMobile();
  const showManagerNotes = useAuthStore((s) => s.hasRole(['manager', 'owner', 'admin']));

  const dialogWidth = useUIStore((s) => s.notesDialogWidth);
  const dialogHeight = useUIStore((s) => s.notesDialogHeight);
  const notesInputHeight = useUIStore((s) => s.notesInputHeight);
  const setNotesDialogSize = useUIStore((s) => s.setNotesDialogSize);
  const setNotesInputHeight = useUIStore((s) => s.setNotesInputHeight);

  const [activeTab, setActiveTab] = useState(0);

  // Customer notes local state
  const [customerNotesLocal, setCustomerNotesLocal] = useState(customerNotesProp ?? '');
  const initialValueRef = useRef(customerNotesProp ?? '');
  const isFocusedRef = useRef(false);
  const updateBooking = useUpdateBooking();

  useEffect(() => {
    // Don't sync if the user is actively editing (field is focused)
    if (isFocusedRef.current) return;
    setCustomerNotesLocal(customerNotesProp ?? '');
    initialValueRef.current = customerNotesProp ?? '';
  }, [customerNotesProp]);

  const handleCustomerNotesChange = (value: string) => {
    setCustomerNotesLocal(value);
    onCustomerNotesChange?.(value);
  };

  const handleCustomerNotesFocus = () => {
    isFocusedRef.current = true;
  };

  const handleCustomerNotesBlurSave = () => {
    isFocusedRef.current = false;
    if (creationMode) return;
    if (customerNotesLocal === initialValueRef.current) return;
    updateBooking.mutate(
      { id: bookingId, data: { customerNotes: customerNotesLocal } },
      {
        onSuccess: () => {
          initialValueRef.current = customerNotesLocal;
          notify.success(t('notesHistory.customerNotesSaved'));
        },
        onError: (error) => notify.error(tc('error'), describeApiError(error, tc, { fallback: t('noteErrors.customerNotesSaveFailed') })),
      },
    );
  };

  // Resize handle state
  const paperRef = useRef<HTMLDivElement>(null);
  const isDraggingResize = useRef(false);
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ w: 0, h: 0 });

  const handleResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    isDraggingResize.current = true;
    resizeStartPos.current = { x: e.clientX, y: e.clientY };
    const paper = paperRef.current;
    resizeStartSize.current = {
      w: paper?.offsetWidth ?? window.innerWidth * 0.8,
      h: paper?.offsetHeight ?? window.innerHeight * 0.9,
    };
  }, []);

  const handleResizePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingResize.current) return;
    const maxW = window.innerWidth - VIEWPORT_MARGIN;
    const maxH = window.innerHeight - VIEWPORT_MARGIN;
    const newW = Math.min(maxW, Math.max(MIN_DIALOG_WIDTH, resizeStartSize.current.w + (e.clientX - resizeStartPos.current.x)));
    const newH = Math.min(maxH, Math.max(MIN_DIALOG_HEIGHT, resizeStartSize.current.h + (e.clientY - resizeStartPos.current.y)));
    const paper = paperRef.current;
    if (paper) {
      paper.style.width = `${String(newW)}px`;
      paper.style.height = `${String(newH)}px`;
    }
  }, []);

  const handleResizePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDraggingResize.current) return;
    isDraggingResize.current = false;
    const maxW = window.innerWidth - VIEWPORT_MARGIN;
    const maxH = window.innerHeight - VIEWPORT_MARGIN;
    const newW = Math.min(maxW, Math.max(MIN_DIALOG_WIDTH, resizeStartSize.current.w + (e.clientX - resizeStartPos.current.x)));
    const newH = Math.min(maxH, Math.max(MIN_DIALOG_HEIGHT, resizeStartSize.current.h + (e.clientY - resizeStartPos.current.y)));
    setNotesDialogSize(newW, newH);
  }, [setNotesDialogSize]);

  // Splitter state (single splitter spanning all columns)
  const isDraggingSplitter = useRef(false);
  const splitterStartY = useRef(0);
  const splitterStartHeight = useRef(0);
  const dragInputHeightRef = useRef<number | null>(null);
  const [dragInputHeight, setDragInputHeight] = useState<number | null>(null);

  const effectiveInputHeight = dragInputHeight ?? notesInputHeight ?? DEFAULT_INPUT_HEIGHT;

  const handleSplitterPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    isDraggingSplitter.current = true;
    splitterStartY.current = e.clientY;
    splitterStartHeight.current = effectiveInputHeight;
  }, [effectiveInputHeight]);

  const handleSplitterPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingSplitter.current) return;
    const delta = splitterStartY.current - e.clientY;
    const newHeight = Math.round(Math.min(MAX_INPUT_HEIGHT, Math.max(MIN_INPUT_HEIGHT, splitterStartHeight.current + delta)));
    dragInputHeightRef.current = newHeight;
    setDragInputHeight(newHeight);
  }, []);

  const handleSplitterPointerUp = useCallback(() => {
    if (!isDraggingSplitter.current) return;
    isDraggingSplitter.current = false;
    if (dragInputHeightRef.current !== null) {
      setNotesInputHeight(dragInputHeightRef.current);
      setDragInputHeight(null);
      dragInputHeightRef.current = null;
    }
  }, [setNotesInputHeight]);

  const { data: notes = [], isLoading } = useBookingNotes(creationMode ? undefined : bookingId);
  const addNote = useAddBookingNote();
  const deleteNote = useDeleteBookingNote();
  const updateNote = useUpdateBookingNote();
  const pinNote = usePinBookingNote();

  const visibleChatCategories = showManagerNotes
    ? CHAT_CATEGORIES
    : CHAT_CATEGORIES.filter((c) => c !== 'manager');

  // All visible tab categories (customer + chat categories)
  const allTabCategories: NoteCategory[] = ['customer', ...visibleChatCategories];

  const categoryLabels: Record<NoteCategory, string> = {
    customer: t('notesHistory.customerTab'),
    internal: t('notesHistory.internalTab'),
    manager: t('notesHistory.managerTab'),
  };

  const countByCategory = (cat: NoteCategory) => {
    if (creationMode) {
      return localNotes?.filter((n) => n.category === cat).length ?? 0;
    }
    return notes.filter((n) => n.category === cat).length;
  };

  const handleSend = (category: NoteCategory, content: string) => {
    if (creationMode) {
      onLocalNoteAdd?.(category, content);
      return;
    }
    addNote.mutate(
      { bookingId, category, content },
      {
        onSuccess: () => notify.success(t('notesHistory.noteSent')),
        onError: () => notify.error(tc('error'), t('notesHistory.noteSendFailed')),
      },
    );
  };

  const handleDelete = (noteId: string) => {
    deleteNote.mutate(
      { bookingId, noteId },
      {
        onSuccess: () => notify.success(t('notesHistory.noteDeleted')),
        onError: (error) => notify.error(tc('error'), describeApiError(error, tc, { fallback: t('noteErrors.deleteFailed') })),
      },
    );
  };

  const handleUpdate = async (noteId: string, content: string) => {
    try {
      await updateNote.mutateAsync({ bookingId, noteId, content });
      notify.success(t('notesHistory.noteUpdated'));
    } catch {
      notify.error(tc('error'), t('notesHistory.noteUpdateFailed'));
    }
  };

  const handleTogglePin = (noteId: string, pinned: boolean) => {
    pinNote.mutate(
      { bookingId, noteId, pinned },
      {
        onSuccess: () => {
          notify.success(pinned ? t('notesHistory.notePinned') : t('notesHistory.noteUnpinned'));
        },
        onError: () => notify.error(tc('error'), t('notesHistory.notePinFailed')),
      },
    );
  };

  const pinnedCount = notes.filter((n) => n.isPinned === true).length;

  const notesTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="h6">{t('notesHistory.notesTitle')}</Typography>
      {pinnedCount > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<StarIcon />}
          label={t('notesHistory.pinnedCount', { count: pinnedCount })}
        />
      )}
    </Box>
  );

  const notesList = !creationMode && notes.length > 0 ? (
    <Box sx={{ maxHeight: 220, overflowY: 'auto', mb: 1, flexShrink: 0 }}>
      <WorkOrderNotesList notes={notes} onTogglePin={handleTogglePin} />
    </Box>
  ) : null;

  const renderCustomerColumn = (isMobileView?: boolean) => (
    <Box
      key="customer"
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(!isMobileView && {
          borderRight: visibleChatCategories.length > 0 ? 1 : 0,
          borderColor: 'divider',
        }),
        px: 1,
      }}
    >
      {!isMobileView && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
          {t('notesHistory.customerTab')}
        </Typography>
      )}
      <TextField
        fullWidth
        multiline
        value={customerNotesLocal}
        onChange={(e) => { handleCustomerNotesChange(e.target.value); }}
        onFocus={handleCustomerNotesFocus}
        onBlur={creationMode ? undefined : handleCustomerNotesBlurSave}
        placeholder={t('create.customerNotesPlaceholder')}
        sx={{
          flex: 1,
          display: 'flex',
          '& .MuiInput-root': {
            flex: 1,
            alignItems: 'flex-start',
          },
          '& .MuiInputBase-input': {
            overflow: 'auto !important',
          },
        }}
      />
    </Box>
  );

  const renderChatColumn = (category: NoteCategory, isLastChat: boolean) => {
    const categoryNotes = creationMode ? [] : notes.filter((n) => n.category === category);
    const categoryLocalNotes = localNotes?.filter((n) => n.category === category) ?? [];
    const count = countByCategory(category);
    const label = count > 0 ? `${categoryLabels[category]} (${String(count)})` : categoryLabels[category];

    return (
      <Box
        key={category}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: isLastChat ? 0 : 1,
          borderColor: 'divider',
          px: 1,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <NotesThread
            category={category}
            notes={categoryNotes}
            isLoading={!creationMode && isLoading}
            onSend={(content) => { handleSend(category, content); }}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            isSending={addNote.isPending}
            isUpdating={updateNote.isPending}
            creationMode={creationMode}
            localNotes={categoryLocalNotes}
            onLocalNoteAdd={(content) => { onLocalNoteAdd?.(category, content); }}
            inputHeight={effectiveInputHeight}
          />
        </Box>
      </Box>
    );
  };

  // Mobile: tab-based fallback
  const renderMobile = () => {
    const currentCategory = allTabCategories[activeTab] ?? 'customer';

    if (currentCategory === 'customer') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v: number) => { setActiveTab(v); }}
            variant="fullWidth"
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
          >
            {allTabCategories.map((cat) => {
              const count = cat !== 'customer' ? countByCategory(cat) : 0;
              const label = count > 0 ? `${categoryLabels[cat]} (${String(count)})` : categoryLabels[cat];
              return <Tab key={cat} label={label} />;
            })}
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 0, pt: 1 }}>
            {renderCustomerColumn(true)}
          </Box>
        </Box>
      );
    }

    const categoryNotes = creationMode ? [] : notes.filter((n) => n.category === currentCategory);
    const categoryLocalNotes = localNotes?.filter((n) => n.category === currentCategory) ?? [];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v: number) => { setActiveTab(v); }}
          variant="fullWidth"
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
        >
          {allTabCategories.map((cat) => {
            const count = cat !== 'customer' ? countByCategory(cat) : 0;
            const label = count > 0 ? `${categoryLabels[cat]} (${String(count)})` : categoryLabels[cat];
            return <Tab key={cat} label={label} />;
          })}
        </Tabs>
        <Box sx={{ flex: 1, minHeight: 0, pt: 1 }}>
          <NotesThread
            category={currentCategory}
            notes={categoryNotes}
            isLoading={!creationMode && isLoading}
            onSend={(content) => { handleSend(currentCategory, content); }}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            isSending={addNote.isPending}
            isUpdating={updateNote.isPending}
            creationMode={creationMode}
            localNotes={categoryLocalNotes}
            onLocalNoteAdd={(content) => { onLocalNoteAdd?.(currentCategory, content); }}
          />
        </Box>
      </Box>
    );
  };

  // Build dynamic paper styles for desktop
  const clampedWidth = dialogWidth ? Math.min(dialogWidth, window.innerWidth - VIEWPORT_MARGIN) : undefined;
  const clampedHeight = dialogHeight ? Math.min(dialogHeight, window.innerHeight - VIEWPORT_MARGIN) : undefined;

  const paperSx = {
    height: clampedHeight ?? '90vh',
    ...(clampedWidth ? { width: clampedWidth } : {}),
    position: 'relative' as const,
    backgroundImage: 'none',
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth={clampedWidth ? false : 'lg'}
      fullWidth={!clampedWidth}
      mobileTitle={
        pinnedCount > 0
          ? `${t('notesHistory.notesTitle')} · ${t('notesHistory.pinnedCount', { count: pinnedCount })}`
          : t('notesHistory.notesTitle')
      }
      PaperProps={{
        ref: paperRef,
        sx: paperSx,
      }}
    >
      {!isMobile && (
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {notesTitle}
            <Tooltip title={tc('close')}>
              <IconButton onClick={onClose} size="small" aria-label={tc('close')}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
      )}

      <DialogContent dividers sx={{ p: isMobile ? 1 : 2, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
        {notesList}
        {isMobile ? (
          renderMobile()
        ) : (
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 0, position: 'relative' }}>
            {/* Customer notes — simple text field */}
            {renderCustomerColumn()}

            {/* Chat columns for internal / manager */}
            {visibleChatCategories.map((cat, idx) =>
              renderChatColumn(cat, idx === visibleChatCategories.length - 1)
            )}

            {/* Single splitter spanning all columns */}
            <Box
              onPointerDown={handleSplitterPointerDown}
              onPointerMove={handleSplitterPointerMove}
              onPointerUp={handleSplitterPointerUp}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: effectiveInputHeight,
                height: 8,
                cursor: 'row-resize',
                touchAction: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                '&:hover > div, &:active > div': { bgcolor: 'action.selected' },
              }}
            >
              <Box sx={{ width: 32, height: 3, borderRadius: 1.5, bgcolor: 'divider', transition: 'background-color 0.15s' }} />
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Resize handle — desktop only */}
      {!isMobile && (
        <Box
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: 20,
            height: 20,
            cursor: 'nwse-resize',
            touchAction: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            '&::after': {
              content: '""',
              width: 10,
              height: 10,
              borderRight: '2px solid',
              borderBottom: '2px solid',
              borderColor: 'divider',
              opacity: 0.6,
            },
            '&:hover::after': {
              opacity: 1,
            },
          }}
        />
      )}
    </ResponsiveDialog>
  );
}
